import React, { useEffect, useMemo, useContext } from 'react';
import EditorBaseTheme from '../DaEditor/components/BaseTheme';
import isEqual from 'react-fast-compare';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Notifications from '../Notifications';
import LiveNotifications from '../LiveNotifications';
import ErrorBoundary from '../ErrorBoundary';
import Modals from '../Modals';
import Head from './Head';
import { SisuContext } from '../Context';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { ThemeContext, ThemeSkinContext } from '@wix/da-react-context/pkg/ThemeContext';
import { MatureContext } from '@wix/da-react-context/pkg/MatureContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { HeadingLevelContext } from '@wix/da-a11y/pkg/components/headings/context';
import { UniqueIdContext, makeUniqueIdSource } from '@wix/da-hooks/pkg/useUniqueId';
import FlagSwitcher from '../redux/flags/FlagSwitcher';
import { FirstTimeFlag } from '../redux/firstTimeFlags';
import VerificationPopup from '../VerificationPopup';
import DuperbrowsePageWrapper from '../Duperbrowse/DuperbrowsePageWrapper';
import { BrowserFeatureContext } from '@wix/da-react-context/pkg/BrowserFeatureContext';
import { NCContext } from '@wix/da-react-context/pkg/NCContext';
import { usePageVisibility } from '@wix/da-hooks/pkg/usePageVisibility';
import { ScreenSizeContextProvider } from '../Measured';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import LocalizedCookieBanner from '../Popup/LocalizedCookieBanner';
import ChatInit from '../Chat/ChatInit';
import DRE from '../DRE';
import HoistCss from './HoistCss';
import TimeTravel from '../TimeTravel';
import AdInit from '@wix/da-ads/pkg/AdInit';
import ConsentManagementInit from '../Ads/cmp/ConsentManagementInit';
/**
 * This is designed to wrap your render() output in App.tsx
 * it provides the common components that we need for some
 * shared things.
 *
 * Things it includes:
 *
 * - <Notifications /> (for general system notifications when deleting, adding to groups, faving, etc)
 * - <LiveNotifications /> (for real time notifications pulled from hub)
 * - <Modals />
 *
 * The dream
 * This component wraps all the header/context etc but currently
 * there is some variation between projects, so for now its limited
 * in its scope.
 */ const DaApp = ({ appMounted, appUnmounted, appVisibilityChange, modalsRenderFunc, showMatureContent = true, isMobile = false, theme, themeSkin, language, environment, bodyClassName, isDuperbrowseEnabled = true, isDuperbrowseActive, isLegacyHeader, supportsWebp = false, supportsLazy = false, publicSession, biPageContextData, disableHeadTags, isLoggedIn, firstTimeFlags, showCookieBanner, children, notificationsRenderFunc, notificationsWrapperClassName, disablePageWideNotifications, disableChat, disableFloatingChat, disableSSRPopups, flags, duperbrowseViewType, isIframe, appUrl, ncContextProps = {}, currentAdSafety })=>{
    useEffect(()=>{
        appMounted();
        return appUnmounted;
    }, [
        appMounted,
        appUnmounted
    ]);
    usePageVisibility(appVisibilityChange);
    const browserSupport = useMemo(()=>({
            supportsWebp,
            supportsLazy
        }), [
        supportsLazy,
        supportsWebp
    ]);
    const uniqueIdSource = useMemo(()=>makeUniqueIdSource('app-root'), []);
    const ncContextPropsMemo = useMemo(()=>ncContextProps, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        ncContextProps.DrawerComponent,
        ncContextProps.PageComponent,
        ncContextProps.isStandalonePage
    ]);
    const renderPageContent = ()=>{
        return /*#__PURE__*/ React.createElement(React.Fragment, null, children, !disablePageWideNotifications && /*#__PURE__*/ React.createElement(Notifications, {
            wrapperClassName: notificationsWrapperClassName
        }, notificationsRenderFunc), /*#__PURE__*/ React.createElement(LiveNotifications, null));
    };
    const renderSSRPopups = ()=>{
        const unverified = isLoggedIn && !publicSession.isVerified;
        const hasNotSeenVerificationPopup = firstTimeFlags[FirstTimeFlag.WelcomePopupNotClosed];
        if (unverified && hasNotSeenVerificationPopup) {
            return /*#__PURE__*/ React.createElement(VerificationPopup, {
                dropFlagOnClose: true
            });
        }
    };
    const isSisu = useContext(SisuContext);
    const onClient = typeof window !== 'undefined';
    const usingQuantcastCMP = onClient && typeof window.__uspapi !== 'undefined';
    const isAbleToShowCookieBanner = !isIframe && !isSisu && onClient && usingQuantcastCMP;
    const testNewCookieBanner = typeof window !== 'undefined' && window.location.href.endsWith('?test_new_cookie_banner=1');
    // the error boundary here is really a last ditch effort - if the error
    // got here the entire page is dead already so we're just saving face
    return /*#__PURE__*/ React.createElement(ErrorBoundary, {
        debugComponent: "DaApp"
    }, /*#__PURE__*/ React.createElement(SessionContext.Provider, {
        value: publicSession
    }, !disableHeadTags && /*#__PURE__*/ React.createElement(Head, {
        environment: environment,
        language: language,
        theme: theme,
        bodyClassName: bodyClassName,
        themeSkin: themeSkin
    }), environment && [
        'dev',
        'rc'
    ].includes(environment) && /*#__PURE__*/ React.createElement(FlagSwitcher, null), /*#__PURE__*/ React.createElement(EditorBaseTheme, null), /*#__PURE__*/ React.createElement(HoistCss, null), /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: biPageContextData
    }, /*#__PURE__*/ React.createElement(BiLoggerContextProvider, {
        value: {
            unsafe: currentAdSafety
        }
    }, /*#__PURE__*/ React.createElement(BrowserFeatureContext.Provider, {
        value: browserSupport
    }, /*#__PURE__*/ React.createElement(MatureContext.Provider, {
        value: showMatureContent
    }, /*#__PURE__*/ React.createElement(MobileContext.Provider, {
        value: isMobile
    }, /*#__PURE__*/ React.createElement(HeadingLevelContext.Provider, {
        value: 1
    }, /*#__PURE__*/ React.createElement(UniqueIdContext.Provider, {
        value: uniqueIdSource
    }, /*#__PURE__*/ React.createElement(DndProvider, {
        backend: HTML5Backend
    }, /*#__PURE__*/ React.createElement(ThemeContext.Provider, {
        value: theme
    }, /*#__PURE__*/ React.createElement(ThemeSkinContext.Provider, {
        value: themeSkin
    }, /*#__PURE__*/ React.createElement(ScreenSizeContextProvider, null, /*#__PURE__*/ React.createElement(NCContext.Provider, {
        value: ncContextPropsMemo
    }, isDuperbrowseEnabled ? /*#__PURE__*/ React.createElement(DuperbrowsePageWrapper, {
        viewType: duperbrowseViewType
    }, renderPageContent()) : renderPageContent(), /*#__PURE__*/ React.createElement(Modals, null, modalsRenderFunc), !disableSSRPopups && renderSSRPopups(), (testNewCookieBanner || showCookieBanner && isAbleToShowCookieBanner) && /*#__PURE__*/ React.createElement(LocalizedCookieBanner, null), !disableChat && /*#__PURE__*/ React.createElement(ChatInit, {
        disableFloatingChat: disableFloatingChat
    }), flags.includes('dre') && !isLegacyHeader && /*#__PURE__*/ React.createElement(DRE, {
        environment: environment
    }), flags.includes('time_travel') && !isLegacyHeader && /*#__PURE__*/ React.createElement(TimeTravel, null), /*#__PURE__*/ React.createElement(AdInit, null), /*#__PURE__*/ React.createElement(ConsentManagementInit, null)))))))))))))));
};
DaApp.displayName = 'DaApp';
export default /*#__PURE__*/ React.memo(DaApp, isEqual);
